import { useState, useEffect } from 'react';
import { ListBase, useTranslate, WithListContext } from 'ra-core';
import { Offer } from '../types';
import OfferCard from './OfferCard';
import { Navigate, useParams, useNavigate } from 'react-router-dom';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import { HeaderLeft, HeaderActions } from '@/components/admin';
import { cn } from '@/lib/utils';
import { Search } from 'lucide-react';
import {
  OfferStatusFilterDropdown,
  matchesOfferFilter,
  type OfferFilterStatus,
} from './OfferStatusFilterDropdown';
import { FinishedOffersTable } from './FinishedOffersTable';
import { FinishedOffersPagination } from './FinishedOffersPagination';

const ALLOWED_STATUSES = ['active', 'finished'];
const DEFAULT_STATUS = 'active';
const FINISHED_PER_PAGE = 10;

const TAB_FILTER_PRIORITY: Record<string, number> = {
  pending: 0,
  accepted: 1,
};

function sortActiveOffers(offers: Offer[]): Offer[] {
  return [...offers].sort((a, b) => {
    const pa = TAB_FILTER_PRIORITY[a.tab_filter] ?? 1;
    const pb = TAB_FILTER_PRIORITY[b.tab_filter] ?? 1;
    return pa - pb;
  });
}

function OfferCardSkeleton() {
  return (
    <div className="rounded-xl border bg-card shadow-premium p-4">
      <div className="flex items-start gap-3 mb-4 mt-1">
        <Skeleton className="w-12 h-12 rounded-lg shrink-0" />
        <div className="flex-1 space-y-2 mt-0.5">
          <Skeleton className="h-4 w-3/5" />
          <Skeleton className="h-3.5 w-2/5" />
        </div>
      </div>
      <div className="flex gap-6">
        <div className="space-y-1.5">
          <Skeleton className="h-3 w-16" />
          <Skeleton className="h-4 w-20" />
        </div>
        <div className="space-y-1.5">
          <Skeleton className="h-3 w-16" />
          <Skeleton className="h-4 w-14" />
        </div>
      </div>
    </div>
  );
}

function OfferListEmpty({ status }: { status: string }) {
  const t = useTranslate();
  const key = status === 'finished' ? 'ended' : 'pending';
  return (
    <div className="col-span-full flex flex-col items-center justify-center gap-4 py-24 text-center">
      <h4 className="text-xl font-semibold text-foreground">
        {t(`empty.list.offers.${key}.title`)}
      </h4>
      <p className="text-sm text-muted-foreground max-w-sm">
        {t(`empty.list.offers.${key}.description`)}
      </p>
    </div>
  );
}

function OfferListTabs({ status }: { status: string }) {
  const { publisherId } = useParams<{ publisherId: string }>();
  const navigate = useNavigate();

  return (
    <HeaderLeft>
      <Tabs
        value={status}
        onValueChange={value =>
          navigate(`/publisher/${publisherId}/offers/${value}`)
        }
      >
        <TabsList className="canvas-bg border border-border w-full sm:w-[332px] h-11 rounded-md p-1 gap-1 shadow-[var(--shadow-well)]">
          {[
            { id: 'active', isLive: true },
            { id: 'finished', isLive: false },
          ].map(({ id, isLive }) => (
            <TabsTrigger
              key={id}
              value={id}
              className={cn(
                'flex-1 gap-2 p-2 rounded-sm data-[state=inactive]:hover:text-foreground capitalize',
                'focus-visible:outline-none focus-visible:ring-0 focus-visible:border-transparent',
                isLive &&
                  'dark:[&[data-state=active]>span]:shadow-[0_0_6px_1px_color-mix(in_oklab,var(--gl-emerald)_55%,transparent)]'
              )}
            >
              <span
                className={cn(
                  'size-[5px] rounded-full transition-all duration-200 ease-premium',
                  isLive ? 'bg-emerald' : 'bg-muted-foreground'
                )}
              />
              {id}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>
    </HeaderLeft>
  );
}

interface OfferListActionsProps {
  status: string;
  selectedStatuses: OfferFilterStatus[];
  onStatusChange: (s: OfferFilterStatus[]) => void;
  searchQuery: string;
  onSearchChange: (q: string) => void;
}

function OfferListActions({
  status,
  selectedStatuses,
  onStatusChange,
  searchQuery,
  onSearchChange,
}: OfferListActionsProps) {
  return (
    <HeaderActions>
      {status === 'active' && (
        <OfferStatusFilterDropdown
          selectedStatuses={selectedStatuses}
          onStatusChange={onStatusChange}
        />
      )}
      <div className="relative flex items-center">
        <Search className="absolute left-3 size-4 text-muted-foreground pointer-events-none" />
        <input
          value={searchQuery}
          onChange={e => onSearchChange(e.target.value)}
          placeholder="Search by name..."
          className="h-11 w-[200px] rounded-md border border-border canvas-bg pl-9 pr-3 text-sm font-medium tracking-tight shadow-[var(--shadow-well)] transition-colors hover:border-border-hover focus:outline-none focus:border-border-hover placeholder:text-muted-foreground/60"
        />
      </div>
    </HeaderActions>
  );
}

export const OfferList = () => {
  const { status: statusInUrl, publisherId } = useParams<{
    status: string;
    publisherId: string;
  }>();

  const status = statusInUrl ?? DEFAULT_STATUS;
  const [selectedStatuses, setSelectedStatuses] = useState<OfferFilterStatus[]>(
    []
  );
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    setSelectedStatuses([]);
    setSearchQuery('');
  }, [status]);

  if (!ALLOWED_STATUSES.includes(status)) {
    return (
      <Navigate
        to={`/publisher/${publisherId}/offers/${DEFAULT_STATUS}`}
        replace
      />
    );
  }

  if (status === 'finished') {
    return (
      <div className="p-4 flex flex-col gap-4">
        <ListBase<Offer>
          key="offers-finished"
          resource="offers"
          filter={{ status: 'ended' }}
          perPage={FINISHED_PER_PAGE}
          queryOptions={{ refetchOnWindowFocus: false }}
          disableSyncWithLocation
        >
          <OfferListTabs status={status} />
          <OfferListActions
            status={status}
            selectedStatuses={[]}
            onStatusChange={() => {}}
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
          />
          <FinishedOffersTable searchQuery={searchQuery} />
          <WithListContext<Offer>
            render={({ total }) =>
              total && total > 0 ? <FinishedOffersPagination /> : null
            }
          />
        </ListBase>
      </div>
    );
  }

  return (
    <div className="p-4 flex flex-col gap-4">
      <ListBase<Offer>
        key="offers-active"
        resource="offers"
        filter={{ status: 'active_tab' }}
        perPage={1000}
        queryOptions={{ refetchOnWindowFocus: false, enabled: Boolean(status) }}
        disableSyncWithLocation
      >
        <OfferListTabs status={status} />
        <OfferListActions
          status={status}
          selectedStatuses={selectedStatuses}
          onStatusChange={setSelectedStatuses}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
        />
        <WithListContext<Offer>
          render={({ data: offers, isFetching, isLoading, total }) => {
            const showSkeleton = isLoading || offers === undefined;

            const sorted = sortActiveOffers(offers ?? []);

            const filtered =
              selectedStatuses.length > 0
                ? sorted.filter(o => matchesOfferFilter(o, selectedStatuses))
                : sorted;

            const displayed = searchQuery.trim()
              ? filtered.filter(o =>
                  o.name
                    .toLowerCase()
                    .includes(searchQuery.trim().toLowerCase())
                )
              : filtered;

            return (
              <div className="relative">
                {isFetching && !showSkeleton && (
                  <div className="absolute top-0 left-0 w-full h-0.5 bg-border rounded-full overflow-hidden z-10">
                    <div className="h-full w-1/3 bg-foreground/25 rounded-full animate-shimmer" />
                  </div>
                )}
                <div className="grid gap-3 sm:grid-cols-[repeat(auto-fill,minmax(350px,1fr))]">
                  {showSkeleton ? (
                    Array.from({ length: 4 }).map((_, i) => (
                      <OfferCardSkeleton key={i} />
                    ))
                  ) : (
                    <>
                      {displayed.map((offer, i) => (
                        <OfferCard {...offer} key={offer.id} _index={i} />
                      ))}
                      {total === 0 && <OfferListEmpty status={status} />}
                    </>
                  )}
                </div>
              </div>
            );
          }}
        />
      </ListBase>
    </div>
  );
};
